//****************************************************************************
//
// Copyright(C) 2025, CyberTAN Technology, Inc. All Rights Reserved.
// This source code contains confidential and proprietary
// information of CyberTAN Technology, Inc. Unauthorized reproduction,
// modification, distribution, or disclosure of this file, in whole or in part,
// by any means, is strictly prohibited without prior written permission from
// CyberTAN Technology, Inc.
//
// THIS SOURCE CODE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
// EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
// MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NONINFRINGEMENT.
//
// IN NO EVENT SHALL CYBERTAN TECHNOLOGY, INC. BE LIABLE FOR ANY CLAIM,
// DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT,
// TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOURCE CODE.
//
//****************************************************************************

import { Consts } from 'models/consts';
import React, { useEffect, useState } from 'react';
import { getItem, setItem } from 'utils/localStorageHelper';

type DefaultConfig = Consts & {
  cloudUrl: string;
  cloudApiUrl: string;
  cbtApiUrl: string;
  developmentRttyUrl: string;
  defaultUserEmail: string;
};

interface ConfigContextType extends DefaultConfig {
  getDefaultConfig: () => void;
}

interface ConfigProviderType {
  children: React.ReactNode;
}

const ConfigContext = React.createContext<ConfigContextType>(null!);

export const ConfigProvider = ({ children }: ConfigProviderType) => {
  const storagDefaultConfig = getItem('defaultConfig') ?? '{}';
  const [defaultConfig, setDefaultConfig] = useState<ConfigContextType>(JSON.parse(storagDefaultConfig));

  const getDefaultConfig = async () => {
    try {
      const response = await fetch(`${process.env.PUBLIC_URL}/config.json`, {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      });
      const json = (await response.json()) as ConfigContextType;
      const constsResponse = await fetch(`${json.cbtApiUrl}api/utils/consts`, { headers: { 'Ronto-SiteId': getItem('siteId') ?? '' } });
      const data = (await constsResponse.json()) as Consts;

      if (json.cloudApiUrl === undefined) {
        if (json.cloudUrl.includes(':3002')) {
          //development mode
          json.cloudApiUrl = `http://${json.cloudUrl}`;
        } else {
          //production mode
          json.cloudApiUrl = `https://${json.cloudUrl}`;
        }
      }

      const nextConfig = Object.assign(json, data);
      setDefaultConfig(nextConfig);
      setItem('defaultConfig', JSON.stringify(nextConfig));
    } catch (error) {
      const err = error as { response?: { data: { message?: string } } };
      console.error(err?.response?.data.message);
    }
  };

  useEffect(() => {
    getDefaultConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <ConfigContext.Provider value={{ ...defaultConfig, getDefaultConfig }}>{children}</ConfigContext.Provider>;
};

export const useConfig = () => React.useContext(ConfigContext);
